<template>
  <div>
    <BaseCard
      :has-divider="true"
      :has-main-container="true"
      base-card-title-class="app-font-semibold title mb-1"
    >
      <template v-slot:header-title>
        Configurações
      </template>

      <v-row
        v-if="isPreviewMode"
        class="file-layout"
      >
        <v-col
          cols="5"
          class="editor-column"
        >
          <div class="editor-sticky">
            <div class="app-align-items-center pb-4 px-2">
              <span class="body-2">
                <strong>Arquivo:</strong> {{ previewFilePath }}
              </span>
              <v-spacer />
              <BaseButton
                type="text"
                color="blueff"
                class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0"
                @click="savePreviewFile()"
              >
                Salvar e atualizar prévia
              </BaseButton>
              <BaseButton
                type="text"
                color="blueff"
                class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0"
                @click="closePreview()"
              >
                Fechar prévia
              </BaseButton>
            </div>
            <BaseCodemirror
              ref="cm-file-config"
              v-model="selectedFile.fileContent"
              cm-ref="cm-file-config"
              title="Visualização prévia"
              :cm-height="600"
              :lang="cmMode"
              :read-only="false"
            />
          </div>
        </v-col>
        <v-divider vertical />
        <v-col
          cols="7"
          class="preview-column"
        >
          <div class="preview-scroll">
            <v-alert
              v-if="isPreviewStale"
              type="warning"
              dense
              text
            >
              Prévia desatualizada — salve o arquivo pra atualizar.
            </v-alert>
            <DashboardLivePreview
              ref="dashboardLivePreview"
              class="pt-4"
              :yaml-content="selectedFile.fileContent"
              :program-id="previewProgramId"
              :config-file-name="previewFilePath ? parseNameDirectory(previewFilePath) : null"
            />
          </div>
        </v-col>
      </v-row>

      <v-row
        v-else
        class="file-layout"
      >
        <v-col
          v-if="!showConfigLogs"
          cols="5"
          class="tree-column"
        >
          <div class="tree-scroll">
            <v-treeview
              :open.sync="openedDirectories"
              :items="configsList"
              item-key="path"
              dense
              hoverable
              class="py-4"
            >
              <template v-slot:label="{ item }">
                <div
                  class="tree-item"
                  :class="{
                    'tree-item--selected':
                      selectedFile &&
                      selectedFile.path === item.path
                  }"
                  style="cursor: pointer;"
                  @click.stop="handleClick(item)"
                >
                  <v-icon
                    class="pr-2"
                    size="18"
                  >
                    {{ item.type == 'directory' ? 'mdi-folder-outline' : 'mdi-file-document-outline' }}
                  </v-icon>
                  {{ item.filename || parseNameDirectory(item.path) }}
                  <BaseMenuOptions
                    :menu-itens="menuItens"
                    :item-data="item"
                    size="x-small"
                    class="mx-2"
                    @onClick="setSelectedFile(item)"
                  />
                </div>
              </template>
            </v-treeview>
          </div>
        </v-col>

        <v-divider vertical />

        <v-col
          :cols="showConfigLogs ? 8 : 7"
          class="editor-column"
        >
          <div class="editor-sticky">
            <div
              v-if="showPreviewFile"
              class="pb-4 px-2"
            >
              <div class="app-align-items-center">
                <v-spacer />
                <BaseButton
                  v-if="!isCreatingFile"
                  :has-icon="true"
                  class="ma-0"
                  :is-disabled="showConfigLogs"
                  @click="getConfigLogs(selectedFile)"
                >
                  <v-icon>mdi-history</v-icon>
                </BaseButton>
              </div>
              <div
                v-if="!isCreatingFile"
                class="app-align-items-center"
              >
                <span class="body-2">
                  <strong>Arquivo:</strong> {{ selectedFile.path }}
                </span>
                <v-spacer />
                <BaseButton
                  v-if="showDiff"
                  type="text"
                  color="blueff"
                  class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0"
                  @click="changeModalContext('restoreLog', diffNewValue)"
                >
                  Restaurar versão
                </BaseButton>
                <BaseButton
                  v-if="!showConfigLogs"
                  type="text"
                  color="blueff"
                  class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0"
                  @click="enableEditFile()"
                >
                  {{ readOnly ? 'Editar arquivo' : 'Cancelar' }}
                </BaseButton>
                <BaseButton
                  v-if="!readOnly"
                  type="text"
                  color="blueff"
                  class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0"
                  @click="saveEditFile('edit')"
                >
                  Salvar
                </BaseButton>
                <BaseButton
                  v-if="!showConfigLogs && isYamlFile"
                  type="text"
                  color="blueff"
                  class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0"
                  @click="openPreview()"
                >
                  Visualizar prévia
                </BaseButton>
              </div>
              <template v-else>
                <v-form
                  ref="formCreateFile"
                  v-model="formCreateFileValid"
                >
                  <div class="app-align-items-center">
                    <span class="body-2">
                      Diretório: <strong>{{ selectedFolder.path }}</strong>
                    </span>
                    <v-spacer />
                    <BaseButton
                      v-if="isYamlFile"
                      type="text"
                      color="blueff"
                      class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0"
                      :is-disabled="!formCreateFileValid"
                      @click="openPreview()"
                    >
                      Visualizar prévia
                    </BaseButton>
                    <BaseButton
                      type="text"
                      color="blueff"
                      class="app-button__link-text app-button--hide-ripple app-button--no-hover px-0"
                      :is-disabled="!formCreateFileValid"
                      @click="saveEditFile('create')"
                    >
                      Criar arquivo
                    </BaseButton>
                  </div>
                  <BaseInput
                    v-model="selectedFile.path"
                    :rules="[isValid, isFileNameValid]"
                    label="Nome do arquivo com extensão"
                    class="my-4"
                    @change="getCmMode(selectedFile.path)"
                  />
                </v-form>
              </template>
              <BaseCodemirror
                v-if="!showDiff"
                ref="cm-file-config"
                v-model="selectedFile.fileContent"
                cm-ref="cm-file-config"
                title="Visualização prévia"
                :cm-height="600"
                :lang="cmMode"
                :read-only="readOnly"
              />
              <CodeDiff
                v-else
                :old-string="copyOfSelectedFile.fileContent"
                :new-string="diffNewValue.fileContent"
                language="JSON"
                output-format="side-by-side"
              />
            </div>
            <div
              v-else
              class="empty-state"
            >
              <img
                src="@/assets/no-file-select.svg"
                width="200"
              >
              <p class="mt-4 grey--text text--darken-1">
                Nenhum arquivo selecionado
              </p>
            </div>
          </div>
        </v-col>

        <template v-if="showConfigLogs">
          <v-divider vertical />
          <v-col cols="4">
            <div class="app-align-items-center mt-4 mb-5">
              <p class="font-weight-bold">
                Histórico de versão
              </p>
              <v-spacer />
              <BaseButton
                :has-icon="true"
                class="ma-0"
                @click="closeLog()"
              >
                <v-icon>mdi-close</v-icon>
              </BaseButton>
            </div>
            <v-divider class="py-2" />
            <v-virtual-scroll
              v-if="mutantConfigLogs.content.length"
              :items="mutantConfigLogs.content"
              height="600"
              item-height="100"
              class="mb-4 pb-2"
            >
              <template v-slot:default="{ item }">
                <v-card
                  elevation="0"
                  class="pa-2 my-2"
                  active-class="tree-item--selected"
                  @click="selectLog(item)"
                >
                  <div class="app-align-items-center mb-3">
                    <p class="font-weight-bold body-1">
                      {{ item.timestamp | getDate }} - {{ item.timestamp | getFullTime }}
                    </p>
                    <v-spacer />
                    <v-chip x-small>
                      {{ item.action }}
                    </v-chip>
                  </div>
                  <p class="body-1">
                    {{ parseInitiatedBy(item) }}
                  </p>
                </v-card>
              </template>
            </v-virtual-scroll>
            <div
              v-else
              class="text-center my-12"
            >
              Nenhum histórico encontrado.
            </div>
          </v-col>
        </template>
      </v-row>
      <BaseSnackbar
        :active-snackbar="feedback"
        :type-snackbar="feedbackColor"
        :text-snackbar="feedbackMessage"
        @closeSnackbar="feedback = !feedback"
      />
    </BaseCard>
    <v-dialog
      v-model="isModalActive"
      persistent
      max-width="560"
    >
      <BaseModal
        :show-indeterminate-progress="modalConfig.showIndeterminateProgress"
        :top-slot-content-title="modalConfig.topSlotContentTitle"
        :remove-button-close="modalConfig.removeButtonClose"
        :middle-slot-content-text-highlight="modalConfig.middleSlotContentTextHighlight"
        :middle-slot-content-text="modalConfig.middleSlotContentText"
        :is-middle-slot-content-text-centered="false"
        :bottom-buttons-align="modalConfig.bottomButtonsAlign"
        :bottom-slot-content-button-cancel-label="modalConfig.bottomSlotContentButtonCancelLabel"
        :bottom-slot-content-button-confirm-label="modalConfig.bottomSlotContentButtonConfirmLabel"
        :remove-button-cancel="modalConfig.removeButtonCancel"
        :remove-button-confirm="modalConfig.removeButtonConfirm"
        :custom-enable-bottom-button-confirm-label="modalConfig.valid"
        @on-cancel="modalConfig.onCancel"
        @on-confirm="modalConfig.onConfirm"
      >
        <v-form
          v-if="showModalContent"
          ref="formModalConfig"
          v-model="modalConfig.valid"
        >
          <template v-if="modalConfig.context == 'uploadFile'">
            <p>Tem certeza que deseja carregar um arquivo no diretório abaixo?</p>
            <p
              v-if="selectedFolder"
              class="app-align-items-center my-4 pa-1"
              :style="{ 'background-color': 'var(--v-grayf5-base)' }"
            >
              <v-icon class="mr-2">
                mdi-folder-arrow-up-outline
              </v-icon>
              {{ selectedFolder.path }}
            </p>
            <p class="text-center ma-4">
              Upload de arquivo
            </p>
            <BaseDragAndDrop
              :upload-configuration="uploadConfig"
              :upload-path="uploadPath"
              @onUpload="onUpload"
            />
          </template>
          <template v-if="modalConfig.context == 'createDirectory'">
            <p>Após a criação do diretório, é necessário adicionar pelo menos um arquivo, caso contrário, <strong>o diretório será descartado</strong>.</p>
            <BaseInput
              v-model="modelCreateDirectory"
              :rules="[isValid, isDirectoryValid]"
              label="Novo diretório"
              :prefix="`${selectedFolder}/`"
              class="my-4"
            />
          </template>
        </v-form>
      </BaseModal>
    </v-dialog>
  </div>
</template>
<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import BaseSnackbarConfiguration from '../../../mixins/BaseSnackbarConfiguration'
import BaseSnackbar from '../../BaseSnackbar'
import BaseMenuOptions from '../../BaseMenuOptions'
import BaseModal from '../../BaseModal'
import BaseDragAndDrop from '../../BaseDragAndDrop'
import BaseCodemirror from '../../BaseCodemirror'
import DashboardLivePreview from './DashboardLivePreview.vue'
import UtilsCodemirror from '../../../mixins/UtilsCodemirror'
import { CodeDiff } from 'v-code-diff'

export default {
  components: { BaseSnackbar, BaseMenuOptions, BaseModal, BaseDragAndDrop, BaseCodemirror, DashboardLivePreview, CodeDiff },
  mixins: [ BaseSnackbarConfiguration, UtilsCodemirror ],
  props: {
    configParameter: {
      type: Boolean,
      required: false,
      default: false
    },
    itemToOpen: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      treeDirectory: [],
      openedDirectories: [],
      selectedFile: null,
      copyOfSelectedFile: null,
      selectedFolder: null,
      uploadPath: null,
      isModalActive: false,
      isCreatingFile: false,
      formCreateFileValid: false,
      readOnly: true,
      cmMode: null,
      isPreviewMode: false,
      previewFilePath: null,
      pendingLeaveNext: null,
      modelCreateDirectory: '',
      modalConfig: {
        context: '',
        valid: true,
        showIndeterminateProgress: false,
        topSlotContentTitle: "",
        removeButtonClose: false,
        middleSlotContentTextHighlight: "",
        middleSlotContentText: "",
        middleCheckAgree: null,
        bottomButtonsAlign: "right",
        bottomSlotContentButtonCancelLabel: "Cancelar",
        bottomSlotContentButtonConfirmLabel: "Carregar",
        removeButtonCancel: false,
        removeButtonConfirm: false,
        onCancel: () => {},
        onConfirm: () => {}
      },
      uploadConfig: {
        deletable: true,
        multiple: false,
      },
      showConfigLogs: false,
      showDiff: false,
      diffNewValue: ''
    }
  },
  computed: {
    ...mapState({
      configsList: state => state.common.configsList,
      mutantConfigLogs: state => state.common.mutantConfigLogs
    }),
    showPreviewFile(){
      return this.selectedFile || this.isCreatingFile
    },
    isYamlFile(){
      return this.cmMode === 'yaml' || this.cmMode === 'yml'
    },
    effectiveFilePath(){
      if(this.isCreatingFile){
        return this.selectedFolder && this.selectedFile && this.selectedFile.path
          ? `${this.selectedFolder.path}/${this.selectedFile.path}`
          : null
      }
      return this.selectedFile ? this.selectedFile.path : null
    },
    hasUnsavedChanges(){
      return Boolean(this.selectedFile) &&
        Boolean(this.copyOfSelectedFile) &&
        this.selectedFile.fileContent !== this.copyOfSelectedFile.fileContent
    },
    isPreviewStale(){
      return this.isPreviewMode && this.hasUnsavedChanges
    },
    previewProgramId(){
      return this.previewFilePath ? this.previewFilePath.split('/')[0] : null
    },
    showModalContent(){
      return this.modalConfig.context == 'uploadFile' || this.modalConfig.context == 'createDirectory'
    },
    menuItens() {
      return [
        {
          text: "Carregar",
          icon: "mdi-upload",
          execute: (item) => {
            this.selectedFolder = item
            this.changeModalContext('uploadFile', item)
          },
          disableButton() { return false },
          hideButton(item){ return item.type !== "directory" }
        },
        {
          text: this.configParameter ? "Inserir" : "Copiar path",
          icon: this.configParameter ? "mdi-content-paste" : "mdi-content-copy",
          execute: async (item) => {
            if(this.configParameter) {
              this.$emit('onInsertPath', item.path)
            } else {
              await navigator.clipboard.writeText(`config://${item.path}`)
            }
          },
          disableButton() { return false },
          hideButton(item){ return item.type == "directory" }
        },
        {
          text: "Criar diretório",
          icon: "mdi-folder-plus-outline",
          execute: (item) => {
            this.selectedFolder = item.path
            this.changeModalContext('createDirectory', item)
          },
          disableButton(item) { return false },
          hideButton(item){ return item.type !== "directory" }
        },
        {
          text: "Criar arquivo",
          icon: "mdi-file-plus-outline",
          execute: (item) => {
            this.selectedFile = {
              path: '',
              fileContent: 'preencha o conteúdo do arquivo...'
            }
            this.selectedFolder = item
            this.isCreatingFile = true
            this.readOnly = false
          },
          disableButton(item) { return false },
          hideButton(item){ return item.type !== "directory" }
        },
        {
          text: "Editar",
          icon: "mdi-pencil",
          execute: () => { this.enableEditFile() },
          disableButton() { return false },
          hideButton(item){ return item.type == "directory" }
        },
        {
          text: "Apagar",
          icon: "mdi-delete",
          tooltipText: "Em breve",
          execute: (item) => {},
          disableButton(item) { return true },
          hideButton(item){ return item.type == "directory" }
        }
      ]
    }
  },
  watch: {
    itemToOpen(val){
      this.openPath(val)
    }
  },
  async created(){
    this.getMutantConfig()
    this.openPath(this.itemToOpen)
  },
  beforeRouteLeave(to, from, next){
    if(this.isPreviewMode && this.isPreviewStale){
      this.changeModalContext('closePreviewUnsaved', null, next)
      return
    }
    if(this.isPreviewMode){
      this.doClosePreview()
    }
    next()
  },
  methods: {
    ...mapActions(['apiMutantConfig', 'apiGetMutantConfigFile', 'apiUploadMutantConfig', 'apiGetMutantConfigLogs']),
    ...mapMutations(['setDisableNavigation']),
    openPreview(){
      this.previewFilePath = this.effectiveFilePath
      this.copyOfSelectedFile = { ...this.selectedFile, path: this.previewFilePath }
      this.isPreviewMode = true
      this.setDisableNavigation(true)
      this.$emit('toggle-preview-fullscreen', true)
    },
    async savePreviewFile(){
      const saved = await this.saveEditFile('edit')
      if(saved){
        this.copyOfSelectedFile = { ...this.copyOfSelectedFile, fileContent: this.selectedFile.fileContent }
        if(this.$refs.dashboardLivePreview){
          this.$refs.dashboardLivePreview.refreshPreview()
        }
      }
    },
    closePreview(){
      if(this.isPreviewStale){
        this.changeModalContext('closePreviewUnsaved')
        return
      }
      this.doClosePreview()
    },
    doClosePreview(){
      this.isPreviewMode = false
      this.previewFilePath = null
      this.setDisableNavigation(false)
      this.$emit('toggle-preview-fullscreen', false)
    },
    async handleClick(item) {
      try {
        if(item.type === 'directory'){
          if(this.isOpenedDirectory(item)) {
            this.closeDirectory(item)
          } else {
            const children = await this.apiMutantConfig(item)
            this.$set(item, 'children', children)
            this.openedDirectories = [
              ...this.openedDirectories,
              item.path
            ]
          }
        } else {
          this.setSelectedFile(item)
        }
      } catch (error) {
        this.showFeedback({
          message: 'Erro ao carregar diretório',
          color: 'error'
        })
      }
    },
    async setSelectedFile(item){
      this.setValuesDefault()
      if (item.type === 'file') {
        try {
          let result = await this.apiGetMutantConfigFile(item.path)
          this.selectedFile = {
            ...item,
            fileContent: this.stringifyValue(result)
          }
          this.getCmMode(this.selectedFile.filename)
        } catch (error) {
          this.showFeedback({
            message: 'Erro para visualizar arquivo',
            color: 'error'
          })
        }
      } else {
        this.selectedFile = null
      }
    },
    onCancelModal(){
      this.isModalActive = false
      this.uploadPath = null
      this.selectedFolder = null
      this.modelCreateDirectory = ''
    },
    async getMutantConfig(){
      await this.apiMutantConfig()
    },
    async onUpload(value, msg = null){
      let message = `${value ? 'Sucesso ' : 'Erro '} ao carregar arquivo.`
      if(value) {
        this.closeDirectory(this.selectedFolder)
        this.handleClick(this.selectedFolder)
      }
      this.onCancelModal()
      this.showFeedback({
        message: msg || message,
        color: value ? 'success' : 'error'
      })
    },
    enableEditFile(){
      if(!this.readOnly){
        this.cancelEditFile()
        return
      }
      this.copyOfSelectedFile = { ...this.selectedFile }
      this.readOnly = false
    },
    cancelEditFile(){
      if(this.hasUnsavedChanges){
        this.changeModalContext('cancelEditUnsaved')
        return
      }
      this.doCancelEditFile()
    },
    doCancelEditFile(){
      this.setCodemirrorValue('cm-file-config', this.copyOfSelectedFile.fileContent)
      this.readOnly = true
    },
    async saveEditFile(context){
      let payload = null
      let selected = null
      let msgContext = {
        "edit": "editar",
        "create": "criar",
        "restore": "restaurar"
      }
      try {
        if(context == 'edit'){
          payload = {
            "path": this.copyOfSelectedFile.path,
            "content": this.encodeByte(this.selectedFile.fileContent)
          }
          selected = {
            path: this.selectedFile.path.replace(`/${this.selectedFile.filename}`, ''),
            type: 'directory'
          }
        } else if(context == 'create'){
          payload = {
            "path": `${this.selectedFolder.path}/${this.selectedFile.path}`,
            "content": this.encodeByte(this.selectedFile.fileContent)
          }
          selected = this.selectedFolder
        } else if(context == 'restore'){
          payload = {
            "path": this.copyOfSelectedFile.path,
            "content": this.encodeByte(this.diffNewValue.fileContent)
          }
          selected = {
            path: this.selectedFile.path.replace(`/${this.selectedFile.filename}`, ''),
            type: 'directory'
          }
        }
        await this.apiUploadMutantConfig(payload)
        this.setValuesDefault()
        this.closeDirectory(selected)
        this.handleClick(selected)
        this.showFeedback({
          message: `Sucesso ao ${msgContext[context]} arquivo.`,
          color: 'success'
        })
        return true
      } catch (error) {
        this.showFeedback({
          message: `Erro ao ${msgContext[context]} arquivo. Tente novamente.`,
          color: 'error'
        })
        return false
      }
    },
    parseNameDirectory(path){
      return path.split('/').reverse()[0]
    },
    isOpenedDirectory(item){
      return this.openedDirectories.includes(item.path)
    },
    closeDirectory(item){
      if (this.isOpenedDirectory(item)) {
        this.openedDirectories = this.openedDirectories.filter(path => !path.includes(item.path))
      }
    },
    isDirectoryValid(value){
      return /^[A-Za-z0-9_-]+$/.test(String(value)) || 'Não são permitidos espaços ou caracteres especiais, apenas hífen e underline';
    },
    isFileNameValid(value){
      return /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9]+)$/.test(String(value)) || 'Preencha o nome do arquivo e a extensão (ex: arquivo-teste.xml)';
    },
    changeModalContext(context, item, next){
      if(context == 'uploadFile'){
        Object.assign(this.modalConfig, {
          ...this.modalConfig,
          context: context,
          topSlotContentTitle: "Carregar arquivo",
          bottomSlotContentButtonConfirmLabel: "Carregar",
          onCancel: () => this.onCancelModal(),
          onConfirm: () => {
            this.uploadPath = this.selectedFolder.path
          }
        })
      } else if(context == 'createDirectory'){
        Object.assign(this.modalConfig, {
          ...this.modalConfig,
          context: context,
          topSlotContentTitle: "Criar diretório",
          bottomSlotContentButtonConfirmLabel: "Criar",
          onCancel: () => this.onCancelModal(),
          onConfirm: async () => {
            if(!this.isOpenedDirectory(item)){
              await this.handleClick(item)
            }
            item.children.unshift({
              "type": "directory",
              "path": `${item.path}/${this.modelCreateDirectory}`
            })
            this.onCancelModal()
          }
        })
      } else if(context == 'restoreLog'){
        let logDate = `${this.$options.filters.getDate(item.timestamp)} - ${this.$options.filters.getFullTime(item.timestamp)}`
        Object.assign(this.modalConfig, {
          ...this.modalConfig,
          context: context,
          topSlotContentTitle: "Restaurar Histórico",
          middleSlotContentText: `<p>Você selecionou a versão do dia <b>${logDate}</b> do arquivo <b>${this.selectedFile.path}</b>.</p><br /><p><b>Ao restaurar, você vai substituir o conteúdo do arquivo pela versão selecionada</b>.</p><br /><p>Essa ação não poderá ser desfeita.</p>`,
          bottomSlotContentButtonConfirmLabel: "Restaurar",
          onCancel: () => this.onCancelModal(),
          onConfirm: async () => {
            await this.saveEditFile('restore')
            this.showDiff = false
            this.showConfigLogs = false
            this.onCancelModal()
            setTimeout(() => {
              this.setCodemirrorValue('cm-file-config', this.stringifyValue(this.diffNewValue.fileContent))
            }, 200)
          }
        })
      } else if(context == 'closePreviewUnsaved'){
        this.pendingLeaveNext = next || null
        Object.assign(this.modalConfig, {
          ...this.modalConfig,
          context: context,
          topSlotContentTitle: "Descartar alterações da prévia?",
          middleSlotContentText: "<p>Você editou o arquivo durante a prévia e ainda não salvou.</p><br /><p><b>Ao fechar sem salvar, essas alterações serão descartadas.</b></p>",
          bottomSlotContentButtonConfirmLabel: "Descartar e fechar",
          onCancel: () => {
            this.onCancelModal()
            if(this.pendingLeaveNext){
              this.pendingLeaveNext(false)
              this.pendingLeaveNext = null
            }
          },
          onConfirm: () => {
            this.selectedFile.fileContent = this.copyOfSelectedFile.fileContent
            this.onCancelModal()
            this.doClosePreview()
            if(this.pendingLeaveNext){
              this.pendingLeaveNext()
              this.pendingLeaveNext = null
            }
          }
        })
      } else if(context == 'cancelEditUnsaved'){
        Object.assign(this.modalConfig, {
          ...this.modalConfig,
          context: context,
          topSlotContentTitle: "Descartar alterações?",
          middleSlotContentText: "<p>Você editou o arquivo e ainda não salvou.</p><br /><p><b>Ao cancelar, essas alterações serão descartadas.</b></p>",
          bottomSlotContentButtonConfirmLabel: "Descartar",
          onCancel: () => this.onCancelModal(),
          onConfirm: () => {
            this.onCancelModal()
            this.doCancelEditFile()
          }
        })
      }
      this.isModalActive = true
    },
    getCmMode(filename){
      this.cmMode = filename.split('.').reverse()[0]
    },
    setValuesDefault(){
      this.readOnly = true
      this.isCreatingFile = false
    },
    async getConfigLogs(value){
      let pathBase64 = this.encodeByte(value.path)
      this.copyOfSelectedFile = { ...this.selectedFile }
      try {
        await this.apiGetMutantConfigLogs({ entity_id: `mutant_config_${pathBase64}` })
        this.showConfigLogs = true
      } catch (error) {
        this.showFeedback({
          message: `Erro ao buscar histórico de versão.`,
          color: 'error'
        })
      }
    },
    selectLog(item){
      let newContent = JSON.parse(item.data.payload)
      this.diffNewValue = {
        ...item,
        fileContent: this.decodeByte(newContent.file_content)
      }
      this.showDiff = true
    },
    closeLog(){
      this.showDiff = false
      this.showConfigLogs = false
      this.setCodemirrorValue('cm-file-config', this.copyOfSelectedFile.fileContent)
    },
    openPath(path){
      this.selectedFile = null
      this.openedDirectories = []
      setTimeout(() => {
        if(path){
          this.findAndOpenItem(path)
        }
      }, 700);
    },
    async findAndOpenItem(value){
      const pathList = value.split('/');
      let currentPath = '';
      let treeList = this.configsList;
      for (const [index, path] of pathList.entries()) {
        currentPath = currentPath ? `${currentPath}/${path}` : path;

        let pathItem = {
          path: currentPath,
          type: index === pathList.length - 1 ? 'file' : 'directory',
        };

        if(pathItem.type === 'directory') {
          let filteredItem = treeList.filter(config => config.path == pathItem.path)
          treeList = filteredItem.length ? filteredItem[0] : pathItem
          await this.handleClick(treeList)
        } else {
          pathItem.filename = pathList[pathList.length - 1]
          await this.setSelectedFile(pathItem)
          setTimeout(() => {
            document.getElementsByClassName("tree-item--selected")[0].scrollIntoView({ behavior: 'smooth' });
          }, 400);
        }
        treeList = treeList.children
      }
    }
  }
}
</script>

<style scoped>
.empty-state {
  height: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.tree-item {
  padding: 4px 8px;
  border-radius: 6px;
  transition: .2s;
}

.tree-item--selected {
  background: #40756d1f;
  color: var(--v-primary-base);
  font-weight: 600;
}

.file-layout {
  height: 100vh;
  overflow: hidden;
}

.tree-column {
  height: 100%;
}

.tree-scroll {
  height: 100%;
  overflow-y: auto;
  padding-right: 8px;
}

.editor-column {
  height: 100%;
}

.editor-sticky {
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
}

.editor-sticky:has(.CodeMirror-fullscreen) {
  position: static;
}

.preview-column {
  height: 100%;
}

.preview-scroll {
  height: 100%;
  overflow-y: auto;
  padding: 16px 8px 16px 16px;
}
</style>
